import { ChangeDetectionStrategy, Component, inject, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { AccountMarketplace } from "@front/m19-api-client";
import { AccountMarketplaceEx, OrganizationAccountGroups } from "@front/m19-models";
import { AccountMarketplaceService, OrganizationUsersService } from "@front/m19-services";
import {
  IBadgeComponent,
  IButtonDirective,
  IDataTableComponent,
  IFormFieldComponent,
  IInputDirective,
  ILabelComponent,
  ITableDataSource,
  ITableImports,
  ITableTextFilterConfig,
  MODAL_DATA,
  ModalRef,
  SpinnerComponent,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { AccountSettingsV2Service } from "@m19-board/services/account-settings.service";
import { forkJoin, Observable, switchMap, tap } from "rxjs";
import { OrganizationListRowComponent } from "./organization-list-row/organization-list-row.component";

@Component({
  selector: "app-account-settings-v2",
  imports: [TranslocoDirective, ITableImports, OrganizationListRowComponent, IDataTableComponent, IBadgeComponent],
  templateUrl: "./account-settings.component.html",
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountSettingsComponent {
  private readonly accountSettingsV2Service = inject(AccountSettingsV2Service);
  private readonly organizationUsersService = inject(OrganizationUsersService);

  readonly organizations$ = this.accountSettingsV2Service.organizationsToDisplay$;
  readonly organizations = toSignal(this.organizations$);
  readonly adminOrganizationIds = toSignal(this.organizationUsersService.adminOrganizationIds$, {
    initialValue: new Set<number>(),
  });

  private textFilterConfig = new ITableTextFilterConfig<OrganizationAccountGroups>((o, filter) => {
    const toLowerCaseFilter = filter.toLowerCase();

    const hasAccountWithAccountId = o.accountGroups.some((g) =>
      g.resources.some((r) => this.passesFilter(r.accountId, toLowerCaseFilter)),
    );
    if (hasAccountWithAccountId) return true;

    const orgId = this.passesFilter(o.organization.organizationId?.toString(), toLowerCaseFilter);
    if (orgId) return true;

    return this.passesFilter(o.organizationName, toLowerCaseFilter);
  });

  readonly dataSource = new ITableDataSource<OrganizationAccountGroups>(
    this.organizations$,
    undefined,
    undefined,
    this.textFilterConfig,
  );

  private passesFilter(value: string | undefined, filter: string) {
    if (!value) return false;
    return value.toLowerCase().includes(filter.toLowerCase());
  }
}

export interface ModalCreateAccountGroupData {
  selectedAccounts: AccountMarketplace[];
}

@Component({
  templateUrl: "../account-settings/new-account-group-popup.component.html",
  imports: [
    TranslocoDirective,
    FormsModule,
    IButtonDirective,
    IFormFieldComponent,
    IInputDirective,
    ILabelComponent,
    SpinnerComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalCreateAccountGroupComponent {
  private accountMarketplaceService = inject(AccountMarketplaceService);
  private toasterService = inject(ToasterService);

  loading = signal(false);
  modalData = inject(MODAL_DATA) as ModalCreateAccountGroupData;
  modalRef = inject(ModalRef);

  name = signal("");
  selection = new Set<string>();

  moveToNewAccountGroup() {
    this.loading.set(true);

    this.accountMarketplaceService
      .createAccountGroup(this.modalData.selectedAccounts![0], this.name())
      .pipe(
        switchMap((gid) => {
          this.modalRef.close();

          const moveAccounts: Observable<AccountMarketplaceEx[]>[] = [];
          for (let i = 0; i < this.modalData.selectedAccounts!.length; i++) {
            const moveAccountRequest = this.accountMarketplaceService
              .moveAccountToGroup(this.modalData.selectedAccounts![i], gid, this.name())
              .pipe(
                tap({
                  error: (error: string) => {
                    this.toasterService.error(error, "Account group creation error");
                  },
                }),
              );
            moveAccounts.push(moveAccountRequest);
          }
          this.selection?.clear();
          return forkJoin(moveAccounts);
        }),
      )
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.toasterService.success(
            "Account group " + this.name() + " successfully created.",
            "Account group created",
          );
        },
        error: (error: string) => {
          this.toasterService.error(error, "Account Group Creation");
          this.modalRef.close();
          this.loading.set(false);
        },
      });
  }

  create() {
    this.moveToNewAccountGroup();
    this.modalRef.close(true);
  }
}
